import * as React from 'react';
import {Flex, FormDropdown, Header, Text} from "@fluentui/react-northstar";
import {IDraftMessage, IMessageChannel} from "../../../Interfaces/Message";
import languageObject from "../../../translations";
import {AppContext, IAppContextState} from "../../../services/AppContext";

interface IConfigurationProps {
    draftMessage: IDraftMessage,
    onDefaultLanguageChange: (languageId: string) => void,
    onAddOrRemoveTranslation: (value: Array<string>) => void,
    onTopicsChange: (value: Array<string>) => void,
    onCanalChange: (value: IMessageChannel) => void,
    onGptChange: (value: string) => void,
}

class Configuration extends React.PureComponent<IConfigurationProps> {
    // Define component context
    static contextType = AppContext;
    context!: IAppContextState;

    render() {
        // Available languages for translations, only english
        const availableLanguages = this.context.languages.list;
        // Default language
        const language = this.context.languages.list.find(l => l.id === this.props.draftMessage.defaultLanguage.id);
        const isGptChannel = this.props.draftMessage.channel === IMessageChannel.Channel_GPTPro;

        const channels = [
            {header: languageObject.translate("Channel_Teams"), value: IMessageChannel.Channel_Teams},
            {
                header: languageObject.translate("Channel_Windows"),
                value: IMessageChannel.Channel_Windows,
                disabled: !this.context.tenant.enableWindows
            },
            {
                header: languageObject.translate("Channel_GPTPro"),
                value: IMessageChannel.Channel_GPTPro,
                disabled: !this.context.tenant.enableGpt
            }
        ]

        return (
            <Flex className="form_layout" fill gap={"gap.small"}>
                <Flex fill column>
                    <Header as="h3" content={languageObject.translate("MessageConfiguration")}
                            styles={{marginBottom: "0"}}/>
                    {/* Default Language Dropdown */}
                    <FormDropdown
                        disabled
                        className={"form-input"}
                        inverted
                        fluid
                        value={languageObject.translate(language?.languageName as string)}
                        label={languageObject.translate("DefaultLanguage")}
                        items={[{
                            header: languageObject.translate(language?.languageName as string),
                            value: this.props.draftMessage.defaultLanguage.id
                        }]}
                        onChange={(e, data) => this.props.onDefaultLanguageChange((data.value as any).value as string)}
                    />
                    {/* Translations Dropdown */}
                    <FormDropdown
                        className={"form-input"}
                        inverted
                        fluid
                        multiple
                        value={this.props.draftMessage.translations.map(t => ({
                            header: languageObject.translate(this.context.languages.list.find(l => l.id === t.id)?.languageName ?? ""),
                            value: this.context.languages.list.find(l => l.id === t.id)?.id
                        }))}
                        label={languageObject.translate("AdditionalLanguages")}
                        items={availableLanguages
                            .filter(l => l.id !== this.props.draftMessage.defaultLanguage.id)
                            .map(l => ({
                                header: languageObject.translate(l.languageName),
                                value: l.id
                            }))}
                        onChange={(e, data) => this.props.onAddOrRemoveTranslation((data.value as any).map((v: any) => v.value))}
                    />
                    {/* Topics Dropdown */}
                    <FormDropdown
                        className={"form-input"}
                        inverted
                        fluid
                        multiple
                        value={this.props.draftMessage.topicsId.map(tid => {
                            const topic = this.context.topics.list.find(t => t.id === tid);
                            return {header: topic?.title, value: topic?.id}
                        })}
                        label={<Text content={<>
                            {languageObject.translate("Topics")}
                            <Text color={"red"} content={" *"}/>
                        </>
                        }/>}
                        items={this.context.topics.list.map(t => ({
                            header: t.title,
                            value: t.id
                        }))}
                        onChange={(e, data) => this.props.onTopicsChange((data.value as any).map((v: any) => v.value))}
                        errorMessage={this.props.draftMessage.topicsId.length > 0 ? "" : languageObject.translate("MustSelectOneTopic")}
                        noResultsMessage={languageObject.translate("NoTopics")}
                    />
                    {/* Canal Dropdown */}
                    {(this.context.tenant.enableWindows || this.context.tenant.enableGpt) &&
                        <FormDropdown
                            className={"form-input"}
                            inverted
                            fluid
                            value={channels.find(c => c.value === this.props.draftMessage.channel)}
                            label={
                                <Text content={
                                    <>
                                        {languageObject.translate("Channel")}
                                        <Text color={"red"} content={" *"}/>
                                    </>
                                }/>
                            }
                            items={channels.filter(c => !c.disabled)}
                            onChange={(_, data) => this.props.onCanalChange((data.value as any).value as IMessageChannel)}
                        />
                    }

                    {isGptChannel && (
                        <FormDropdown
                            className={"form-input"}
                            inverted
                            fluid
                            value={this.context.gpts.list.find(c => c.id === this.props.draftMessage.gptId)?.fullName}
                            label={<Text content={<>
                                {languageObject.translate("Gpt")}
                                <Text color={"red"} content={" *"}/>
                            </>}/>}
                            items={this.context.gpts.list.map(t => ({
                                header: t.fullName,
                                value: t.id
                            }))}
                            onChange={(_, data) => this.props.onGptChange((data.value as any).value)}
                            errorMessage={this.props.draftMessage.gptId?.length! > 0 ? "" : languageObject.translate("MustSelectGpt")}

                        />
                    )}

                </Flex>
                <div style={{display: "none"}} className="adaptiveCardContainer"/>
            </Flex>
        );
    }
}

export default Configuration